// frontend/src/hooks/useReviewForm.js
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { getReview, updateReview, createReview, fetchReviewSchema, suggestCompanies } from "../api";
import { computeScore, levelToApiLabel } from "@backend-lib/riskEngine.js";
import { RISK_THRESHOLDS } from "@backend-lib/riskSchema.js";

const REVIEW_STORAGE_KEY = "review_draft";

const SECTIONS = ["A", "B"];

const ERROR_TYPES = {
  NETWORK: "NETWORK",
  AUTH: "AUTH",
  SERVER: "SERVER",
  VALIDATION: "VALIDATION",
  UNKNOWN: "UNKNOWN",
};

const RETRY_CONFIG = { maxAttempts: 3, baseDelay: 1000, maxDelay: 10000 };

function classifyError(error) {
  if (!navigator.onLine) {
    return { type: ERROR_TYPES.NETWORK, message: "No internet connection.", userMessage: "Connection lost. Please check your internet connection.", canRetry: true };
  }
  if (error?.status === 401 || error?.status === 403) {
    return { type: ERROR_TYPES.AUTH, message: "Authentication failed.", userMessage: "Your session has expired. Please log in again.", canRetry: false };
  }
  if (error?.status >= 500) {
    return { type: ERROR_TYPES.SERVER, message: "Server error.", userMessage: "Our servers are experiencing issues. Please try again later.", canRetry: true };
  }
  if (error?.status >= 400 && error?.status < 500) {
    return { type: ERROR_TYPES.VALIDATION, message: error?.message || "Invalid request", userMessage: error?.message || "Please check your input and try again.", canRetry: false };
  }
  return { type: ERROR_TYPES.UNKNOWN, message: error?.message || "Unexpected error", userMessage: "Something went wrong. Please try again.", canRetry: true };
}

async function withRetry(operation, config = RETRY_CONFIG) {
  let lastError;
  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      const info = classifyError(error);
      if (!info.canRetry || attempt === config.maxAttempts) throw error;
      const delay = Math.min(config.baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000, config.maxDelay);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  throw lastError;
}

function validateForm(companySel, reviewDate, reviewer, nextReviewDate) {
  const errors = [];
  if (!companySel?.id) {
    errors.push({ field: "company", message: "Please select a company from the dropdown" });
  }
  if (!reviewDate) {
    errors.push({ field: "reviewDate", message: "Review date is required" });
  } else {
    const todayStr = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    if (reviewDate > todayStr) {
      errors.push({ field: "reviewDate", message: "Review date cannot be in the future" });
    }
  }
  if (nextReviewDate && reviewDate && nextReviewDate < reviewDate) {
    errors.push({ field: "nextReviewDate", message: "Next review cannot be before the review date" });
  }
  if (reviewer && reviewer.length < 2) {
    errors.push({ field: "reviewer", message: "Reviewer name must be at least 2 characters" });
  }
  return errors;
}

const createStatusState = () => ({ loading: false, error: null, success: false, retryCount: 0 });

export function useReviewForm(reviewId = null) {
  const isEditMode = !!reviewId;
  const autosaveTimerRef = useRef(null);

  // Schema and loading
  const [schemaStatus, setSchemaStatus] = useState({ ...createStatusState(), loading: true });
  const [schema, setSchema] = useState({ QUESTIONS: [], ANSWER_POINTS: null });

  // Form state
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});

  // Company selection
  const [companyQ, setCompanyQ] = useState("");
  const [companySel, setCompanySel] = useState(null);
  const [suggest, setSuggest] = useState([]);
  const [suggestStatus, setSuggestStatus] = useState(createStatusState());

  // Review details
  const today = new Date().toISOString().split("T")[0];
  const [reviewDate, setReviewDate] = useState(today);
  const [nextReviewDate, setNextReviewDate] = useState("");
  const [nextDateTouched, setNextDateTouched] = useState(false);
  const [reviewer, setReviewer] = useState("");

  // UI state
  const [validationErrors, setValidationErrors] = useState([]);
  const [saveStatus, setSaveStatus] = useState(createStatusState());
  const [openCommentId, setOpenCommentId] = useState(null);

  const abortControllerRef = useRef(null);
  const suggestTimeoutRef = useRef(null);

  // Load schema
  const loadSchema = useCallback(async (isRetry = false) => {
    setSchemaStatus((prev) => ({ ...prev, loading: true, error: null, retryCount: isRetry ? prev.retryCount + 1 : 0 }));
    try {
      const data = await withRetry(async () => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();
        return await fetchReviewSchema({ signal: abortControllerRef.current.signal });
      });

      setSchema({
        QUESTIONS: Array.isArray(data?.QUESTIONS) ? data.QUESTIONS : [],
        ANSWER_POINTS: data?.ANSWER_POINTS || null,
      });

      setSchemaStatus({ ...createStatusState(), success: true });
    } catch (error) {
      const info = classifyError(error);
      setSchemaStatus((prev) => ({ ...prev, loading: false, error: info, success: false }));
      setSchema((prev) => ({ QUESTIONS: prev.QUESTIONS || [], ANSWER_POINTS: prev.ANSWER_POINTS || null }));
    }
  }, []);

  // Load existing review data (edit mode)
  const loadReviewData = useCallback(async () => {
    if (!reviewId) return;

    try {
      const review = await getReview(reviewId);

      // Set form fields
      if (review.company_id) {
        // For edit mode, we need to create a company object
        // This is a simplified version - in real app you might want to fetch company details
        setCompanySel({ id: review.company_id, name: `Company ${review.company_id}` });
      }

      setReviewDate(review.review_date ? review.review_date.slice(0, 10) : today);
      setNextReviewDate(review.next_review_date ? review.next_review_date.slice(0, 10) : "");
      setReviewer(review.reviewer || "");

      // Set answers - backend stores as object, form expects object
      setAnswers(review.answers || {});

      // Set comments - backend stores as object, form expects object
      setComments(review.comments_by_question || {});

      // Mark next date as touched if it was set
      if (review.next_review_date) {
        setNextDateTouched(true);
      }
    } catch (error) {
      console.error("Failed to load review:", error);
      // Handle error - maybe redirect back or show error message
    }
  }, [reviewId, today]);

  // Initialize
  useEffect(() => {
    loadSchema();
    if (isEditMode) {
      loadReviewData();
    }
    // Create mode: try load draft from sessionStorage
    if (!isEditMode) {
      try {
        const raw = sessionStorage.getItem(REVIEW_STORAGE_KEY);
        if (raw) {
          const d = JSON.parse(raw);
          if (d && typeof d === "object") {
            if (d.companySel) setCompanySel(d.companySel);
            if (d.reviewDate) setReviewDate(d.reviewDate);
            if (d.nextReviewDate) setNextReviewDate(d.nextReviewDate);
            if (typeof d.nextDateTouched === "boolean") setNextDateTouched(d.nextDateTouched);
            if (typeof d.reviewer === "string") setReviewer(d.reviewer);
            if (d.answers && typeof d.answers === "object") setAnswers(d.answers);
            if (d.comments && typeof d.comments === "object") setComments(d.comments);
            if (typeof d.step === "number") setStep(d.step);
          }
        }
      } catch (_) {
        // ignore corrupt draft
      }
    }

    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current);
      // Always clear timer
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
      // Belt & suspenders: when leaving the form, remove draft (create mode)
      if (!isEditMode) {
        sessionStorage.removeItem(REVIEW_STORAGE_KEY);
      }
    };
  }, [loadSchema, loadReviewData, isEditMode]);

  // Debounced autosave for create mode
  useEffect(() => {
    if (isEditMode) return; // never autosave in edit mode
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      try {
        const payload = {
          companySel,
          reviewDate,
          nextReviewDate,
          nextDateTouched,
          reviewer,
          answers,
          comments,
          step,
        };
        sessionStorage.setItem(REVIEW_STORAGE_KEY, JSON.stringify(payload));
      } catch (_) {
        // storage may fail in very rare cases; safe to ignore
      }
    }, 300);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [isEditMode, companySel, reviewDate, nextReviewDate, nextDateTouched, reviewer, answers, comments, step]);

  // Auto-suggest next review date
  useEffect(() => {
    if (reviewDate && !nextDateTouched) {
      const d = new Date(reviewDate + "T00:00:00");
      d.setFullYear(d.getFullYear() + 1);
      setNextReviewDate(d.toISOString().slice(0, 10));
    }
  }, [reviewDate, nextDateTouched]);

  // Company suggestions
  const fetchCompanySuggestions = useCallback(
    async (query) => {
      // Don't search in edit mode or if company already selected
      if (isEditMode || !query.trim() || companySel) {
        setSuggest([]);
        return;
      }
      setSuggestStatus({ ...createStatusState(), loading: true });
      try {
        const res = await suggestCompanies(query);
        setSuggest(Array.isArray(res) ? res : []);
        setSuggestStatus({ ...createStatusState(), success: true });
      } catch (error) {
        const info = classifyError(error);
        setSuggest([]);
        setSuggestStatus({ ...createStatusState(), error: info });
      }
    },
    [companySel, isEditMode]
  );

  useEffect(() => {
    if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current);
    suggestTimeoutRef.current = setTimeout(() => fetchCompanySuggestions(companyQ), 300);
    return () => {
      if (suggestTimeoutRef.current) clearTimeout(suggestTimeoutRef.current);
    };
  }, [companyQ, fetchCompanySuggestions]);

  // Save function
  const onSave = useCallback(
    async (e, onSuccess) => {
      e?.preventDefault?.();

      const errs = validateForm(companySel, reviewDate, reviewer, nextReviewDate);
      setValidationErrors(errs);
      if (errs.length) {
        const first = errs[0]?.field;
        if (first) {
          const target = document.querySelector(`[name="${first}"]`) || document.querySelector(`#${first}`) || document.querySelector(`[id*="${first}"]`);
          target?.scrollIntoView({ behavior: "smooth", block: "center" });
          target?.focus?.();
        }
        return;
      }

      // Convert answers object -> array for API (if needed)
      const answersArray = Object.entries(answers)
        .filter(([, v]) => v === "Y" || v === "N" || v === "NA")
        .map(([id, value]) => ({ id, value }));

      // Build payload
      const basePayload = {
        company_id: companySel.id,
        review_date: reviewDate,
        ...(nextReviewDate ? { next_review_date: nextReviewDate } : {}),
        ...(reviewer ? { reviewer } : {}),
        answers: answersArray,
        comments_by_question: comments,
      };

      setSaveStatus((s) => ({ ...s, loading: true, error: null, success: false }));

      // ✅ Clear draft on successful save
      try {
        sessionStorage.removeItem(REVIEW_STORAGE_KEY);
      } catch (_) {}

      try {
        if (isEditMode) {
          // Update existing review
          await withRetry(async () => updateReview(reviewId, basePayload));
        } else {
          // Create new review
          await withRetry(async () => createReview(basePayload));
        }

        setSaveStatus({ loading: false, error: null, success: true, retryCount: 0 });

        // Call success callback (for navigation)
        if (onSuccess) {
          setTimeout(onSuccess, 500); // Brief delay to show success state
        } else {
          setTimeout(() => setSaveStatus({ loading: false, error: null, success: false, retryCount: 0 }), 3000);
        }
      } catch (err) {
        const info = classifyError(err);
        setSaveStatus({ loading: false, error: info, success: false, retryCount: 0 });
      }
    },
    [companySel, reviewDate, nextReviewDate, reviewer, answers, comments, isEditMode, reviewId]
  );

  // Computed values
  const QUESTIONS = schema.QUESTIONS || [];
  const currentSection = SECTIONS[step];
  const score = computeScore({
    answers,
    questions: QUESTIONS,
    schema: schema?.ANSWER_POINTS ? { ANSWER_POINTS: schema.ANSWER_POINTS } : null,
    thresholds: RISK_THRESHOLDS,
  });

  return {
    // State
    isEditMode,
    step,
    setStep,
    answers,
    setAnswers,
    comments,
    setComments,
    companyQ,
    setCompanyQ,
    companySel,
    setCompanySel,
    suggest,
    setSuggest,
    reviewDate,
    setReviewDate,
    nextReviewDate,
    setNextReviewDate,
    nextDateTouched,
    setNextDateTouched,
    reviewer,
    setReviewer,
    validationErrors,
    setValidationErrors,
    openCommentId,
    setOpenCommentId,

    // Status
    schemaStatus,
    suggestStatus,
    saveStatus,

    // Computed
    schema,
    QUESTIONS,
    SECTIONS,
    currentSection,
    score,

    // Actions
    onSave,
    loadSchema,
    fetchCompanySuggestions,
    handleCompanySelect: useCallback((c) => {
      setCompanySel(c);
      setCompanyQ("");
      setSuggest([]);
      setValidationErrors((prev) => prev.filter((e) => e.field !== "company"));
    }, []),
    closeAllComments: useCallback(() => setOpenCommentId(null), []),
  };
}
